import { generatePath, Outlet, useNavigate, useParams } from 'react-router-dom';
import { useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '@store';
import { ButtonPrimary, ButtonSize } from '@ui/src';
import { OrganizationType } from '../types';
import { getCards, onCardStatusChange } from '../store/slices/cards';
import { GeneralInfo } from '../../individual/types';
import { CorporateGeneralInfo } from '../../corporate/types';
import Card from '../../../components/common/card/card';
import CardsList from '../../../components/common/cards/list';
import CustomerCardsTransactions from './parts/list';
import { CardType } from './types';

import ROUTES from '../../../routes';

import cn from './styles.module.css';

const CustomerCards = ({ type }: { type: OrganizationType }) => {
  const {
    cards,
    isLoading,
    statusChangeInProgress,
    updatedCard,
    changeStatusSuccess,
  } = useAppSelector((state) => state.customerCards);

  const { baseInformation: individualCustomerBaseInformation } = useAppSelector(
    (state) => state.individualCustomers.info.general,
  );

  const { baseInformation: corporateCustomerBaseInformation } = useAppSelector(
    (state) => state.corporateCustomers.info.general,
  );

  const { activePermissions } = useAppSelector((state) => state.root);

  const { customerId } = useParams();

  const dispatch = useAppDispatch();

  const navigate = useNavigate();

  const baseInformation =
    type === OrganizationType.INDIVIDUAL
      ? individualCustomerBaseInformation
      : corporateCustomerBaseInformation;

  const formattedCards = useMemo(() => {
    const data = cards?.map((item) => {
      const result = { ...item };

      if (
        type === OrganizationType.INDIVIDUAL ||
        type === OrganizationType.COMPANY_OFFICIAL
      ) {
        result.type = CardType.DEBIT;
      } else {
        result.type = CardType.CORP;
      }

      return result;
    });

    return data;
  }, [cards]);

  useEffect(() => {
    if (!cards) {
      dispatch(getCards());
    }
  }, [dispatch, cards]);

  useEffect(() => {
    let title;

    if (
      type === OrganizationType.INDIVIDUAL ||
      type === OrganizationType.COMPANY_OFFICIAL
    ) {
      title =
        (baseInformation as Partial<GeneralInfo>)?.firstName ||
        (baseInformation as Partial<GeneralInfo>)?.lastName
          ? `${(baseInformation as Partial<GeneralInfo>)?.firstName} ${(baseInformation as Partial<GeneralInfo>)?.lastName || ''}: Cards`
          : 'Cards';
    } else {
      title = (baseInformation as Partial<CorporateGeneralInfo>)?.name
        ? `${(baseInformation as Partial<CorporateGeneralInfo>)?.name}: Cards`
        : 'Cards';
    }

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, [baseInformation, type]);

  let content;

  if (isLoading) {
    content = <Card isLoading={isLoading} />;
  } else if (!isLoading && cards) {
    content = (
      <>
        <ButtonPrimary
          size={ButtonSize.SMALL}
          text="Order a card"
          className={cn.orderCardButton}
          onClick={() =>
            navigate(
              generatePath(
                type === OrganizationType.INDIVIDUAL ||
                  type === OrganizationType.COMPANY_OFFICIAL
                  ? ROUTES.CUSTOMERS_INDIVIDUAL_CARDS_ORDER
                  : ROUTES.CUSTOMERS_CORPORATE_CARDS_ORDER,
                {
                  customerId: customerId as string,
                },
              ),
            )
          }
        />
        <CardsList
          customerId={customerId}
          cards={formattedCards || []}
          onStatusChange={(data) => dispatch(onCardStatusChange(data))}
          updatedCard={updatedCard}
          statusChangeInProgress={statusChangeInProgress}
          customerType={type}
          changeStatusInProgress={statusChangeInProgress}
          changeStatusSuccess={changeStatusSuccess}
          activePermissions={activePermissions}
        />
        {cards?.length > 0 && (
          <CustomerCardsTransactions
            customerType={type}
            cards={formattedCards || []}
          />
        )}
      </>
    );
  }

  return (
    <div>
      {content}
      <Outlet />
    </div>
  );
};

export default CustomerCards;
